/**
 * Campaign Tracker Cookie Manager
 *
 * Handles parsing, reading, writing, and removing the campaign cookie.
 * Includes consent gating logic for GDPR/CCPA compliance.
 *
 * Part of BEC-20520: Campaign Tracker with Salesforce
 */

import { checkAnalyticsConsent } from '@/lib/algoliaUserToken';

import {
  CAMPAIGN_COOKIE_NAME,
  getCampaignCookieMaxAge,
  getCookieAttributes,
  UTM_PARAMS,
} from './constants';
import type { CampaignData } from './types';
import { CONSENT_REQUIRED_COUNTRIES } from './types';

/**
 * Check if the given country requires OneTrust consent for performance cookies.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (case-insensitive)
 * @returns true if consent is required
 */
export const isConsentRequired = (countryCode: string): boolean => {
  return CONSENT_REQUIRED_COUNTRIES.has(countryCode.toUpperCase());
};

/**
 * Check if the campaign cookie can be set based on consent status.
 *
 * - For non-consent regions: always returns true
 * - For consent regions: checks OneTrust C0002 (Performance) category
 *
 * Reuses existing checkAnalyticsConsent() from algoliaUserToken.ts
 * to avoid duplication and ensure consistency.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns true if cookie can be set
 */
export const canSetCampaignCookie = (countryCode: string): boolean => {
  if (!isConsentRequired(countryCode)) {
    return true;
  }

  // Reuse existing consent check for C0002 (Performance cookies)
  return checkAnalyticsConsent();
};

/**
 * Parse UTM parameters from a URL.
 *
 * Returns CampaignData if at least one UTM param is present in the URL.
 * Missing params are set to empty string (not omitted).
 *
 * @param url - Optional URL to parse. If not provided, uses window.location.search
 * @returns CampaignData if any UTM param exists, null otherwise
 */
export const parseUtmParams = (url?: string): CampaignData | null => {
  // SSR safety: return null if no window and no explicit URL
  if (typeof window === 'undefined' && !url) {
    return null;
  }

  try {
    const searchParams = new URLSearchParams(
      url ? new URL(url).search : window.location.search,
    );

    // Check if any UTM param exists (even if empty value)
    const hasAnyUtm = UTM_PARAMS.some((param) => searchParams.has(param));
    if (!hasAnyUtm) {
      return null;
    }

    return {
      utm_source: searchParams.get('utm_source') ?? '',
      utm_medium: searchParams.get('utm_medium') ?? '',
      utm_campaign: searchParams.get('utm_campaign') ?? '',
    };
  } catch {
    // Malformed URL: return null safely
    return null;
  }
};

/**
 * Get the current campaign data from the cookie.
 *
 * @returns CampaignData if cookie exists and is valid, null otherwise
 */
export const getCampaignCookie = (): CampaignData | null => {
  // SSR safety
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const cookies = document.cookie.split(';');
    const campaignCookie = cookies
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${CAMPAIGN_COOKIE_NAME}=`));

    if (!campaignCookie) {
      return null;
    }

    const value = campaignCookie.substring(CAMPAIGN_COOKIE_NAME.length + 1);
    const parsed = JSON.parse(decodeURIComponent(value));

    // Validate structure
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'utm_source' in parsed &&
      'utm_medium' in parsed &&
      'utm_campaign' in parsed
    ) {
      return {
        utm_source: String(parsed.utm_source),
        utm_medium: String(parsed.utm_medium),
        utm_campaign: String(parsed.utm_campaign),
      };
    }

    return null;
  } catch {
    // Malformed cookie: return null safely (no throw)
    return null;
  }
};

/**
 * Set the campaign cookie with UTM data.
 *
 * Cookie attributes (Secure, domain) depend on environment:
 * - PRD: Secure + domain (from getCampaignCookieDomain(), e.g., '.breitling.com' or '.gallet.com')
 * - Non-PRD: host-only (works on localhost)
 *
 * @param data - Campaign data to store
 */
export const setCampaignCookie = (data: CampaignData): void => {
  // SSR safety
  if (typeof document === 'undefined') {
    return;
  }

  try {
    const maxAge = getCampaignCookieMaxAge();
    const value = encodeURIComponent(JSON.stringify(data));
    const attributes = getCookieAttributes(maxAge);

    document.cookie = `${CAMPAIGN_COOKIE_NAME}=${value}; ${attributes}`;
  } catch {
    // Cookie setting failed: fail silently
  }
};

/**
 * Remove the campaign cookie.
 *
 * Used when consent is revoked in consent-required regions.
 */
export const removeCampaignCookie = (): void => {
  // SSR safety
  if (typeof document === 'undefined') {
    return;
  }

  try {
    // Set max-age=0 to delete the cookie
    // Must match the same path (and domain in PRD) to properly delete
    const attributes = getCookieAttributes(0);
    document.cookie = `${CAMPAIGN_COOKIE_NAME}=; ${attributes}`;
  } catch {
    // Cookie removal failed: fail silently
  }
};

/**
 * Check if campaign data has changed compared to existing cookie.
 *
 * @param current - Current cookie data (or null if none)
 * @param newData - New data from URL
 * @returns true if any field differs (or current is null)
 */
export const hasCampaignDataChanged = (
  current: CampaignData | null,
  newData: CampaignData,
): boolean => {
  if (!current) {
    return true;
  }

  return (
    current.utm_source !== newData.utm_source ||
    current.utm_medium !== newData.utm_medium ||
    current.utm_campaign !== newData.utm_campaign
  );
};
